import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { LogOut, Settings, UserRound, Link, Download, Bookmark, Mail } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import { useSubscription } from '@/hooks/useSubscription'

export function Navbar() {
  const { user, signOut, linkWithGoogle } = useAuth()
  const navigate = useNavigate()
  const { canInstall, install } = usePWAInstall()
  const { isPro, isAtLeastLite } = useSubscription()

  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : 'G'

  return (
    <header className="flex h-14 items-center justify-between border-b px-4 md:px-6">
      <div className="md:hidden flex items-center">
        <img src="/icons/quikee.svg" alt="Quikee" width="93" height="24" className="h-6" />
      </div>
      <div className="hidden md:block" />
      <div className="flex items-center gap-2">
        {canInstall && (
          <Button variant="outline" size="sm" onClick={install}>
            <Download className="mr-2 h-4 w-4" />
            Install
          </Button>
        )}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
                <div className={
                  isPro
                    ? 'rounded-full p-[2px] bg-gradient-to-br from-amber-400 to-amber-600'
                    : isAtLeastLite
                    ? 'rounded-full p-[2px] bg-gradient-to-br from-indigo-400 to-indigo-600'
                    : ''
                }>
                  <Avatar className="h-8 w-8 block">
                    <AvatarImage src={user.photoURL ?? undefined} alt={user.displayName ?? 'User'} referrerPolicy="no-referrer" />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="flex items-center gap-2 p-2">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user.displayName ?? 'Guest'}</p>
                  <p className="text-xs text-muted-foreground">
                    {user.email ?? 'Anonymous account'}
                  </p>
                </div>
              </div>
              <DropdownMenuSeparator />
              {user.isAnonymous && (
                <DropdownMenuItem onClick={linkWithGoogle}>
                  <Link className="mr-2 h-4 w-4" />
                  Link Google Account
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/app/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/saved')}>
                <Bookmark className="mr-2 h-4 w-4" />
                Saved Responses
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/invites')}>
                <Mail className="mr-2 h-4 w-4" />
                Invites
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}

export function LandingNavbar({
  onSignIn,
  topOffset = 0,
}: {
  onSignIn: () => void
  topOffset?: number
}) {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  return (
    <header
      style={{ top: topOffset }}
      className="fixed left-0 right-0 z-50 flex h-16 items-center justify-between px-6 bg-background/80 backdrop-blur-sm border-b transition-[top] duration-300"
    >
      <div className="flex items-center">
        <img src="/icons/quikee.svg" alt="Quikee" width="108" height="28" className="h-7" />
      </div>
      <div className="flex items-center gap-3">
        {loading ? (
          <div className="h-9 w-24 animate-pulse rounded-md bg-muted" />
        ) : user ? (
          <Button onClick={() => navigate('/app/dashboard')}>Go to Dashboard</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onSignIn}>
              <UserRound className="mr-2 h-4 w-4" />
              Sign In
            </Button>
            <Button onClick={onSignIn}>Get Started</Button>
          </>
        )}
      </div>
    </header>
  )
}
